import React from "react"
import cx from "classnames"
import Avatar from "../avatar"
import { CountBadge } from "../count-badge"
import { Icon } from "../icon"
import { VisuallyHidden } from "../visually-hidden"
import { useNavigationContext } from "./navigation-context"

// =============================================================================
// Props
// =============================================================================

export type NavigationAccountTriggerProps = {
  /**
   * Display name shown next to the avatar when expanded, and used as the
   * accessible label when the sidebar is collapsed.
   */
  name: string
  /** URL for the account's avatar image. Falls back to initials when absent. */
  avatarUrl?: string
  /**
   * Optional count of unread notifications shown as a badge next to the name.
   * Rendered only when greater than 0 and the sidebar is expanded.
   */
  notificationCount?: number
  /** Additional CSS classes (layout only). */
  className?: string
}

// =============================================================================
// Styles
// =============================================================================

const triggerStyles = [
  "flex w-full cursor-pointer items-center gap-1 rounded-[10px]",
  "ring-1 ring-inset ring-gray-200 p-2 text-left",
  "hover:bg-gray-900/[0.06] data-[popup-open]:bg-gray-900/[0.06]"
].join(" ")

// =============================================================================
// Component
// =============================================================================

/**
 * A trigger button for the user account menu in the sidebar navigation.
 * Shows an avatar, display name, and overflow icon when expanded. Collapses
 * to avatar-only (with a visually-hidden accessible label) when the sidebar
 * is collapsed.
 *
 * Designed to be passed as the `render` prop to `Menu.Trigger` — Base UI
 * merges its event handlers and ARIA attributes onto the underlying button.
 *
 * @example
 * <Menu.Trigger render={<Navigation.AccountTrigger name={user.name} />} />
 */
export const NavigationAccountTrigger = React.forwardRef<
  HTMLButtonElement,
  NavigationAccountTriggerProps & React.ButtonHTMLAttributes<HTMLButtonElement>
>(function NavigationAccountTrigger(
  { name, avatarUrl, notificationCount, className, ...rest },
  ref
) {
  const { collapsed } = useNavigationContext()

  return (
    <button
      ref={ref}
      data-slot="navigation-account-trigger"
      type="button"
      className={cx(triggerStyles, collapsed && "justify-center", className)}
      {...rest}
    >
      <Avatar avatarUrl={avatarUrl} name={name} size="sm" aria-hidden="true" />
      {collapsed ? (
        // The count badge is intentionally omitted when collapsed: the
        // avatar-only rail has no room for the pill.
        <VisuallyHidden>{name} account</VisuallyHidden>
      ) : (
        <>
          <span className="flex-1 truncate pl-1 text-sm font-semibold">
            {name}
            <VisuallyHidden> account</VisuallyHidden>
          </span>
          {notificationCount !== undefined && notificationCount > 0 && (
            <CountBadge count={notificationCount} />
          )}
          <Icon name="menu-vertical" size="md" />
        </>
      )}
    </button>
  )
})

NavigationAccountTrigger.displayName = "Navigation.AccountTrigger"
